// Estado POR ARCHIVO de la última lectura con IA (contrato v2: ContenidoCotizacion.archivos_leidos, lo pone el servidor
// en cada lectura y nunca se edita a mano). Lo usan Recibos (la lista de archivos) y Revisión (el cotejo).
import { ESTADOS_ARCHIVO_LEIDO } from '@cotizador/compartido';
import type { ArchivoLeido, ArchivoSubido, ContenidoCotizacion, EstadoArchivoLeido } from '@cotizador/compartido';

export type TonoArchivo = 'verde' | 'ambar' | 'rojo' | 'neutro';

/** Un Record sobre ESTADOS_ARCHIVO_LEIDO: si el contrato agrega un estado sin su texto, no compila. */
export const ETIQUETA_ARCHIVO_LEIDO: Readonly<Record<EstadoArchivoLeido, { texto: string; tono: TonoArchivo; uno: string; varios: string }>> = {
  leido: { texto: 'Leído', tono: 'verde', uno: 'leído', varios: 'leídos' },
  parcial: { texto: 'Leído en parte', tono: 'ambar', uno: 'leído en parte', varios: 'leídos en parte' },
  ilegible: { texto: 'No se lee', tono: 'rojo', uno: 'no se lee', varios: 'no se leen' },
  repetido: { texto: 'Repetido', tono: 'neutro', uno: 'repetido', varios: 'repetidos' },
  no_es_cfe: { texto: 'No es de CFE', tono: 'rojo', uno: 'no es de CFE', varios: 'no son de CFE' },
  otro_servicio: { texto: 'Otro número de servicio', tono: 'rojo', uno: 'de otro número de servicio', varios: 'de otro número de servicio' },
  no_leible_por_ia: { texto: 'Formato que la IA no lee', tono: 'rojo', uno: 'en formato que la IA no lee', varios: 'en formato que la IA no lee' },
  sin_leer: { texto: 'Sin leer', tono: 'neutro', uno: 'sin leer', varios: 'sin leer' },
  // Diego, 25-oct: al subir recibos nuevos, el más viejo sale del año que se cotiza (se dice, no se calla).
  fuera_del_anio: { texto: 'Fuera del año: no se usó', tono: 'ambar', uno: 'fuera del año que se cotiza', varios: 'fuera del año que se cotiza' },
};

/** "3 leídos · 1 repetido": el resumen de una lista por archivo, lo que pide acción primero. */
export function resumenEstados(cuenta: Partial<Record<EstadoArchivoLeido, number>>, despues: number): string {
  return [
    ...ORDEN_ESTADOS_ARCHIVO.filter((e) => cuenta[e]).map((e) => `${cuenta[e]} ${cuenta[e] === 1 ? ETIQUETA_ARCHIVO_LEIDO[e].uno : ETIQUETA_ARCHIVO_LEIDO[e].varios}`),
    ...(despues ? [`${despues} sin leer todavía`] : []),
  ].join(' · ');
}

/** Orden en que se cuentan los estados en el resumen (lo que pide acción primero). */
export const ORDEN_ESTADOS_ARCHIVO: readonly EstadoArchivoLeido[] = [
  ...ESTADOS_ARCHIVO_LEIDO.filter((e) => ETIQUETA_ARCHIVO_LEIDO[e].tono === 'rojo'),
  ...ESTADOS_ARCHIVO_LEIDO.filter((e) => ETIQUETA_ARCHIVO_LEIDO[e].tono !== 'rojo'),
];

export type EstadoDeArchivo =
  /** La última lectura con IA dice qué pasó con ESTE archivo. */
  | { tipo: 'leido'; leido: ArchivoLeido }
  /** Hubo lectura por archivo, pero este recibo se subió después: hay que volver a leer. */
  | { tipo: 'despues' }
  /** Borrador de antes del contrato v2: solo se sabe si la lectura lo usó. */
  | { tipo: 'usado' }
  /** Nunca se ha leído. */
  | { tipo: 'nada' };

/** Qué se sabe de un archivo según la última lectura. Solo los recibos se mandan a leer. */
export function estadoDeArchivo(b: Pick<ContenidoCotizacion, 'archivos_leidos' | 'lecturas'>, a: Pick<ArchivoSubido, 'id' | 'clase' | 'medidor'>): EstadoDeArchivo {
  if (a.clase !== 'recibo') return { tipo: 'nada' };
  const lista = b.archivos_leidos;
  if (lista && lista.length > 0) {
    const x = lista.find((l) => l.archivo_id === a.id);
    return x ? { tipo: 'leido', leido: x } : { tipo: 'despues' };
  }
  return b.lecturas[a.medidor]?.archivo_ids.includes(a.id) ? { tipo: 'usado' } : { tipo: 'nada' };
}

/** Recibos subidos que la última lectura NO leyó (nunca leídos, o subidos después de la lectura): los que la lectura
 *  automática y el botón «Leer recibos» de Revisión tienen pendientes. Los que la lectura sí vio aunque no pudo leerlos
 *  («Sin leer: es una foto y la IA no tiene llave») no cuentan: volver a leer no los cambia. */
export function recibosSinLeer(b: Pick<ContenidoCotizacion, 'archivos' | 'archivos_leidos' | 'lecturas'>): ArchivoSubido[] {
  return b.archivos.filter((a) => {
    if (a.clase !== 'recibo' || a.eliminado) return false;
    const e = estadoDeArchivo(b, a).tipo;
    return e === 'nada' || e === 'despues';
  });
}

/** Texto corto para la lista de archivos ("Leído · jul 26", "Repetido del recibo 2"…), o null si no hay qué decir. */
export function textoEstado(e: EstadoDeArchivo, numeroDe: (archivoId: string) => number | null): { texto: string; tono: TonoArchivo } | null {
  if (e.tipo === 'nada') return null;
  if (e.tipo === 'usado') return { texto: 'Leído', tono: 'verde' };
  if (e.tipo === 'despues') return { texto: 'Subido después de la lectura', tono: 'ambar' };
  const base = ETIQUETA_ARCHIVO_LEIDO[e.leido.estado];
  // Etapa 4: lo que leyó el lector sin IA (PDF digital de CFE) lo dice: es gratis y no salió de la IA.
  if (e.leido.leido_por === 'reglas' && (e.leido.estado === 'leido' || e.leido.estado === 'parcial')) return { texto: `${base.texto} sin IA`, tono: base.tono };
  if (e.leido.estado === 'repetido' && e.leido.repetido_de) {
    const n = numeroDe(e.leido.repetido_de);
    return { texto: n === null ? base.texto : `Repetido del recibo ${n}`, tono: base.tono };
  }
  return base;
}

/** ¿Hay archivos que no sirvieron o que faltan por leer? (para el aviso de "vuelve a leer" o "cámbialo"). */
export function cuentaPorEstado(lista: readonly ArchivoLeido[]): Partial<Record<EstadoArchivoLeido, number>> {
  const c: Partial<Record<EstadoArchivoLeido, number>> = {};
  for (const a of lista) c[a.estado] = (c[a.estado] ?? 0) + 1;
  return c;
}
